import { Alert, AlertTitle } from "@mui/material";
import * as React from "react";

import { PageLayout } from "@/shared/components";
import { Comments } from "@/shared/components/comments";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { useRecentCommentsSuspense } from "@/shared/domain/comments/hooks/useRecentComments";
import { useUIComments } from "@/shared/domain/comments/hooks/useUIComment";

import { CommentCard } from "./CommentCard";
import { CommentsLoader } from "./CommentsLoader";
import { CommentsLoadError } from "./CommentsLoadError";

export function CommentsRoute() {
  return (
    <PageLayout title={"Roboto - Comments"} headerContentLeft={"All comments"}>
      <ErrorBoundary
        fallback={(props) => <CommentsLoadError error={props.error} />}
      >
        <React.Suspense fallback={<CommentsLoader />}>
          <RecentComments />
        </React.Suspense>
      </ErrorBoundary>
    </PageLayout>
  );
}

function RecentComments() {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState<10 | 25 | 50>(25);

  const commentsQuery = useRecentCommentsSuspense();
  const comments = useUIComments(commentsQuery.data);

  const { hasNextPage, isFetching, fetchNextPage } = commentsQuery;

  // Fetch more pages if needed for pagination
  React.useEffect(() => {
    const neededComments = (page + 1) * rowsPerPage;
    if (comments.length < neededComments && hasNextPage && !isFetching) {
      void fetchNextPage();
    }
  }, [
    page,
    rowsPerPage,
    comments.length,
    hasNextPage,
    isFetching,
    fetchNextPage,
  ]);

  // Paginate comments client-side
  const displayedComments = comments.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10) as 10 | 25 | 50);
    setPage(0);
  };

  if (comments.length === 0) {
    return (
      <Alert severity="info">
        <AlertTitle>No comments yet</AlertTitle>
        Comments will appear here once they are created on datasets, files, or
        other resources.
      </Alert>
    );
  }

  return (
    <div style={{ maxWidth: "960px" }}>
      {displayedComments.map((comment) => (
        <CommentCard key={comment.comment_id} comment={comment} />
      ))}
      <Comments.Pagination
        component="div"
        count={comments.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </div>
  );
}
